/* eslint-disable max-lines */
import { EventBusy } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoIcon from '@mui/icons-material/Info';
import {

  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AppointmentListItemDetails from './AppointmentListItemDetails';
import ModalConfirmation from '../ModalConfirmation';
import VideoConfButton from 'FRONTEND/components/Calendar/VideoConfButton';
import InitialsAvatar from 'FRONTEND/components/InitialsAvatar';
import { RootState } from 'FRONTEND/reducers/types';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { FormattedAppointment } from 'FRONTEND/utils/backendApi/Appointments/appointment.types';
import { getAppointmentExpertNames } from 'FRONTEND/utils/backendApi/Appointments/appointmentUtils';
import { Expert } from 'FRONTEND/utils/backendApi/expert/expert.types';
import {
  APPOINTMENT_STATUS_AWAITING_PAYMENT,
  APPOINTMENT_STATUS_AWAITING_CAPTURE,
  APPOINTMENT_STATUS_CANCELLED,
  OTHER,
  PARTICIPANT_STATUS_NEEDS_ACTION,
  PARTICIPANT_STATUS_TENTATIVE,
  BOOTH_TELECONSULTATION,
} from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { RIGHTS } from 'FRONTEND/utils/rights';


type AppointmentListItemProps = {
  component?: React.ElementType;
  layout?: 'horizontal' | 'vertical';
  onAcceptAppointmentClick?: (appointment: FormattedAppointment) => void;
  onDeclineAppointmentClick?: (appointment: FormattedAppointment) => void;
  onCancelAppointmentClick?: (appointmentId: string) => void;
  onPatientRecordClick?: (appointment: FormattedAppointment) => void;
  onExpertClick?: (expertId: string) => void;
  appointment: FormattedAppointment;
  expert?: Expert;
  userId: string;
  rtcServerUrl: string;
  isSquare?: boolean;
  displayMode?: 'panel' | 'default';
  isPastAppointment?: boolean;
};
type AppointmentStatusLabelProps = {
  icon: React.ElementType;
  label: string;
  color: string;
  layout: 'horizontal' | 'vertical';
};
const AppointmentStatusLabel = ({ icon: Icon, label, color, layout }: AppointmentStatusLabelProps) => {
  return (
    <Stack direction='row' alignItems='center' spacing={0.5} py={layout === 'vertical' ? 1 : 0} px={layout === 'vertical' ? 0 : 1}>
      <Icon fontSize='small' sx={{ color }} />
      <Typography color={color} fontWeight={700} variant='body2'>
        {label}
      </Typography>
    </Stack>
  );
};

const AppointmentListItem = ({
  component,
  layout = 'horizontal',
  onAcceptAppointmentClick = () => {},
  onDeclineAppointmentClick = () => {},
  onCancelAppointmentClick = () => {},
  onPatientRecordClick = () => {},
  onExpertClick = () => {},
  appointment,
  expert,
  userId,
  rtcServerUrl,
  isSquare = true,
  displayMode = 'default',
  isPastAppointment = false,
}: AppointmentListItemProps) => {
  const theme = useTheme();
  const Component = component ?? ListItem;
  const canVideoConf = displayMode === 'default';

  // Refers to appointments created by PatientUsers from the Patient Portal.
  const isPatientRequestedAppointment = appointment.patient.isRequester;
  const isPatientUser = appointment?.patient?.userId === userId;

  // Actions management
  const handleAcceptAppointmentClick = () => {
    onAcceptAppointmentClick(appointment);
  };
  const handleDeclineAppointmentClick = () => {
    onDeclineAppointmentClick(appointment);
  };
  const handleCancelAppointmentClick = () => {
    onCancelAppointmentClick(appointment.id);
  };
  // const handlePatientRecordClick = () => {
  //   onPatientRecordClick(appointment);
  // };

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const handleModalClose = (confirmed: boolean) => {
    setIsCancelModalOpen(false);
    if (confirmed) {
      handleCancelAppointmentClick();
    }
  };

  // We need useCallback to prevent the re-rendering of the avatars which cause blinking
  const handleExpertClick = useCallback(() => {
    onExpertClick(expert?.id);
  }, [onExpertClick, expert?.id]);
  const hasCalendarRight = useSelector(state => selectHasRight(state, RIGHTS.CALENDAR_NAVIGATE));

  const managedUsers = useSelector((state: RootState) => state.expert.managedIdList); // Users who have delegated calendar to current user.
  const canCancelThroughDelegation = appointment.canCancelList.some(id => managedUsers.includes(id)); // Check if current user has delegation over anyone that can cancel.
  const isAppointmentCancelable = (appointment.canCancelList.includes(userId) || canCancelThroughDelegation) && displayMode === 'default';
  const needsAction = [PARTICIPANT_STATUS_NEEDS_ACTION, PARTICIPANT_STATUS_TENTATIVE].includes(appointment.requiredMap?.[appointment.expertId]?.status);

  // Payment status
  const isCharged = useMemo(() => {
    // if appointment has consultation code, that means a fee was defined for it.
    return Boolean(appointment.extension.find(ext => ext.id === 'ConsultationCode'));
  }, [appointment]);
  const isChargedAndAwaitingPayment = isCharged && appointment.status === APPOINTMENT_STATUS_AWAITING_PAYMENT;
  const isChargedAndAwaitingCapture = isCharged && appointment.status === APPOINTMENT_STATUS_AWAITING_CAPTURE;
  const isAwaitingPaymentWithoutCharge = appointment.status === APPOINTMENT_STATUS_AWAITING_PAYMENT && !isCharged;
  const isAwaitingCaptureWithoutCharge = appointment.status === APPOINTMENT_STATUS_AWAITING_CAPTURE && !isCharged;

  const sx: SxProps<Theme> = {
    '&.MuiListItem-secondaryAction': {
      paddingRight: (theme) => canVideoConf ? theme.spacing(20) : (needsAction ? theme.spacing(8) : theme.spacing(1)), // 20 spacings = 2 offset spacings + (6 button spacings * 3 max button count)
    },
  };

  if (layout === 'vertical') {
    sx.display = 'flex';
    sx.flexDirection = 'column';
    sx.alignItems = 'flex-start';
    sx.marginBottom = '10px';
  } else {
    sx.borderRadius = '10px';
    sx.backgroundColor = theme.palette.background.paper;
    sx.display = 'grid';
    sx.gridTemplateColumns = '100px 1fr 1fr 1fr';
  }

  const requiredList = Object.values(appointment.requiredMap || {});
  const avatarInitialsSource = isPatientRequestedAppointment ? requiredList[0]?.display : expert?.alias;

  return (
    <Component
      component={Paper}
      square={isSquare}
      sx={sx}
    >
      <ListItemAvatar sx={{ mr: 1 }}>
        <InitialsAvatar
          picture={isPatientRequestedAppointment ? '' : expert?.picture}
          initials={getInitials(avatarInitialsSource)}
          sideSize={40}
          onClick={hasCalendarRight ? handleExpertClick : undefined}
          additionalUsers={isPatientRequestedAppointment ? undefined : appointment?.requiredMap}
          sx={{ border: (theme) => `2px solid ${theme.palette.background.paper}` }}
        />
      </ListItemAvatar>
      <ListItemText
        sx={{ width: 'auto' }}
        primary={getAppointmentExpertNames(appointment)}
        secondary={
          <>
            <Typography component='span' variant='body2' color='text.primary' sx={{ textDecoration: appointment.status === APPOINTMENT_STATUS_CANCELLED ? 'line-through' : 'none' }}>
              {`${dayjs(appointment.startDate).format(layout === 'vertical' ? 'llll' : 'LLLL')} - ${dayjs(appointment.endDate).format('LT')}`}
            </Typography>
            {appointment.status === APPOINTMENT_STATUS_CANCELLED && (
              <Typography component='span' variant='body2' color='text.primary' fontWeight={700} sx={{ ml: 1 }}>
                {i18n._('Cancelled')}
              </Typography>
            )}
          </>
        }
      />
      <AppointmentListItemDetails appointment={appointment} isPatientUser={isPatientUser} />

      <Stack>
        {appointment.status === APPOINTMENT_STATUS_CANCELLED && (
          <AppointmentStatusLabel icon={HighlightOffIcon} label={i18n._('Cancelled')} color='text.secondary' layout={layout} />
        )}
        {(isChargedAndAwaitingPayment || isAwaitingPaymentWithoutCharge) && (
          <AppointmentStatusLabel icon={HourglassEmptyIcon} label={i18n._('Awaiting payment')} color='warning.main' layout={layout} />
        )}
        {(isChargedAndAwaitingCapture || isAwaitingCaptureWithoutCharge) && (
          <AppointmentStatusLabel icon={InfoIcon} label={i18n._('Awaiting capture')} color='success.main' layout={layout} />
        )}
      </Stack>
      {!isPastAppointment && (
        <ListItemSecondaryAction
          sx={{
            display: 'flex',
            flexDirection: layout === 'vertical' ? 'column' : 'row',
            alignItems: 'center',
          }}
        >
          {needsAction && (
            <>
              <Tooltip title={i18n._('Accept')}>
                <IconButton size='large' color='primary' onClick={handleAcceptAppointmentClick}>
                  <CheckCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n._('Decline')}>
                <IconButton size='large' onClick={handleDeclineAppointmentClick}>
                  <CancelOutlinedIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {isAppointmentCancelable && (
            <Tooltip title={i18n._('Cancel')}>
              <IconButton size='large' onClick={() => setIsCancelModalOpen(true)}>
                <EventBusyIcon />
              </IconButton>
            </Tooltip>
          )}
          {(appointment.appointmentType !== OTHER && appointment.appointmentType !== BOOTH_TELECONSULTATION) && appointment.status !== APPOINTMENT_STATUS_CANCELLED && canVideoConf && (
            <VideoConfButton
              color={appointment.expertId === userId ? 'primary' : 'inherit'}
              appointment={appointment}
              rtcServerUrl={rtcServerUrl}
            />
          )}
        </ListItemSecondaryAction>
      )}
      <ModalConfirmation
        open={isCancelModalOpen}
        onClose={handleModalClose}
        message={i18n._('The cancellation of an appointment is final. Are you sure you want to cancel this appointment?')}
        title={<Stack direction='row' alignItems='center' gap={1}><EventBusy /> {i18n._('Confirm cancellation')}</Stack>}
      />
    </Component>
  );
};

export default memo(AppointmentListItem); // Prevent extra picture rendering by memoizing the component
